import React, { useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  useLocation,
} from 'react-router-dom';
import {
  FiCalendar,
  FiArrowRight,
  FiMessageSquare,
} from 'react-icons/fi';
import './WorkflowBar.css';
import { useAeonasUnread, ChatBadge, unreadDestination } from './AeonasUnread';
import { useAuth } from '../auth/AuthContext';
import { FiMessageCircle } from 'react-icons/fi';
import './WorkflowCustomerSearch.css';
import './BadgeLayout.css';
import './WorkflowChatSpacing.css';
// prime-ink-badge-layout-v1
import ConsultationActionBadges from './ConsultationActionBadges';
import {EMPTY_ACTION_COUNTS, actionSummary, actionLabel, actionTarget} from '../hooks/useConsultationActionCounts';

const STUDIOS = [
  { id: 3, short: 'D', name: 'Dortmund' },
  { id: 2, short: 'M', name: 'Münster' },
  { id: 1, short: 'E', name: 'Essen' },
];

function WorkflowBar({actionCounts = EMPTY_ACTION_COUNTS}) {
  const location = useLocation();
  const { user } = useAuth();
  const chatUnread = useAeonasUnread();
  const navigate = useNavigate();
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const searchInput = useRef(null);
  const barRef = useRef(null);
  const widthTimer = useRef(null);
  const resetDrag = () => {
    const bar = barRef.current;
    bar?.classList.remove('workflow-search-dragging', 'workflow-swipe-ready');
    bar?.style.removeProperty('--workflow-drag-width');
    bar?.style.removeProperty('--workflow-swipe-progress');
  };
  useEffect(() => {
    clearTimeout(widthTimer.current);
    if (!searchExpanded) widthTimer.current = setTimeout(() => barRef.current?.style.removeProperty('--workflow-closed-width'), 260);
    return () => clearTimeout(widthTimer.current);
  }, [searchExpanded]);
  useEffect(() => {
    gesture.current = null;
    suppressClick.current = false;
    resetDrag();
    setSearchExpanded(false);
    setQuery('');
  }, [location.pathname]);
  useEffect(() => {
    const bar = barRef.current;
    if (!searchExpanded || !bar) return undefined;
    const viewport = window.visualViewport;
    const positionSearch = () => {
      const layoutHeight = document.documentElement.clientHeight || window.innerHeight;
      const visibleBottom = viewport ? viewport.height + viewport.offsetTop : window.innerHeight;
      const inset = Math.max(0, layoutHeight - visibleBottom);
      bar.style.setProperty('--workflow-keyboard-inset', `${Math.round(inset)}px`);
    };
    positionSearch();
    viewport?.addEventListener('resize', positionSearch);
    viewport?.addEventListener('scroll', positionSearch);
    window.addEventListener('resize', positionSearch);
    return () => {
      viewport?.removeEventListener('resize', positionSearch);
      viewport?.removeEventListener('scroll', positionSearch);
      window.removeEventListener('resize', positionSearch);
      bar.style.removeProperty('--workflow-keyboard-inset');
    };
  }, [searchExpanded]);
  const closeSearch = () => {
    searchInput.current?.blur();
    setSearchExpanded(false);
    setQuery('');
  };
  const openSearch = () => {
    clearTimeout(widthTimer.current);
    const bar = barRef.current;
    if (!bar) return;
    if (!bar.style.getPropertyValue('--workflow-closed-width')) bar.style.setProperty('--workflow-closed-width', `${bar.getBoundingClientRect().width}px`);
    // Keep the input mounted; reveal and focus within the user gesture.
    setSearchExpanded(true);
    resetDrag();
    if (searchInput.current) {
      searchInput.current.form.hidden = false;
      try { searchInput.current.focus({ preventScroll: true }); } catch (_) { /* Search can still be tapped. */ }
    }
    try { navigator.vibrate?.(12); } catch (_) { /* Visual feedback remains available. */ }
  };
  const startSwipe = event => {
    suppressClick.current = false;
    if (searchExpanded || event.isPrimary === false || event.pointerType === 'touch' || (event.button != null && event.button !== 0) || !window.matchMedia('(max-width: 768px)').matches || !barRef.current?.contains(event.target)) return;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null, progress: 0 };
  };
  const moveSwipe = event => {
    const drag = gesture.current;
    if (!drag || drag.id !== event.pointerId) return;
    const dx = drag.x - event.clientX;
    const dy = Math.abs(drag.y - event.clientY);
    if (!drag.axis) {
      if (Math.max(Math.abs(dx), dy) < 8) return;
      suppressClick.current = true;
      if (dx <= 0 || dy > Math.abs(dx) * .8) { gesture.current = null; return; }
      drag.axis = 'horizontal';
      const bar = barRef.current;
      drag.width = bar.getBoundingClientRect().width;
      drag.targetWidth = Math.max(drag.width, Math.min(420, window.innerWidth - 32));
      clearTimeout(widthTimer.current);
      bar.style.setProperty('--workflow-closed-width', `${drag.width}px`);
      if (event.pointerId != null && !String(event.pointerId).startsWith('touch-')) {
        try { bar.setPointerCapture?.(event.pointerId); } catch (_) { /* Continue without capture when unavailable. */ }
      }
      bar.classList.add('workflow-search-dragging');
    }
    if (event.cancelable) event.preventDefault();
    drag.progress = Math.min(1, Math.max(0, dx / 64));
    const bar = barRef.current;
    bar.style.setProperty('--workflow-swipe-progress', String(drag.progress));
    bar.style.setProperty('--workflow-drag-width', `${drag.width + (drag.targetWidth - drag.width) * drag.progress}px`);
    bar.classList.toggle('workflow-swipe-ready', drag.progress >= .75);
  };
  const endSwipe = event => {
    const drag = gesture.current;
    if (!drag || drag.id !== event.pointerId) return;
    gesture.current = null;
    const dx = drag.x - event.clientX;
    const dy = Math.abs(drag.y - event.clientY);
    if (Math.max(Math.abs(dx), dy) >= 8) suppressClick.current = true;
    if (dx >= 48 && dy <= dx * .8) openSearch();
    else resetDrag();
  };
  const cancelSwipe = () => { gesture.current = null; resetDrag(); };
  const touchEvent = (event, touch) => ({
    target: event.target, pointerId: `touch-${touch.identifier}`, pointerType: 'native-touch',
    clientX: touch.clientX, clientY: touch.clientY,
    // touch-action handles scrolling; React touch listeners may be passive.
    cancelable: false,
  });
  const startTouch = event => {
    if (event.touches.length !== 1) { cancelSwipe(); return; }
    startSwipe(touchEvent(event, event.touches[0]));
  };
  const moveTouch = event => {
    if (event.touches.length !== 1) { cancelSwipe(); return; }
    moveSwipe(touchEvent(event, event.touches[0]));
  };
  const endTouch = event => {
    const touch = Array.from(event.changedTouches).find(item => gesture.current?.id === `touch-${item.identifier}`);
    if (touch) endSwipe(touchEvent(event, touch));
  };
  const search = event => {
    event.preventDefault();
    if (!query.trim()) return;
    const text = query.trim(); closeSearch();
    if (location.pathname === '/') window.dispatchEvent(new CustomEvent('primeink:customer-search', { detail: { query: text } }));
    else { sessionStorage.setItem('primeink:customer-search', text); navigate('/'); }
  };

  const totalActions = actionSummary(actionCounts);

  const isBooking =
    location.pathname === '/';

  const isConsultations =
    location.pathname.startsWith(
      '/consultations'
    );

  return (
    <div
      ref={barRef}
      className={`workflow-bar ${['owner', 'manager'].includes(user?.role) ? 'workflow-has-chat' : ''} ${searchExpanded ? 'workflow-search-expanded' : ''}`}
      onPointerDown={startSwipe} onPointerMove={moveSwipe} onPointerUp={endSwipe} onPointerCancel={event => { if (event.pointerType !== 'touch') cancelSwipe(); }}
      onTouchStart={startTouch} onTouchMove={moveTouch} onTouchEnd={endTouch} onTouchCancel={cancelSwipe}
      onContextMenu={event => event.preventDefault()}
      onClickCapture={event => { if (suppressClick.current) { suppressClick.current = false; event.preventDefault(); event.stopPropagation(); } }}
      aria-label="Schnellnavigation"
    >
      <form className="workflow-customer-search" hidden={!searchExpanded} onSubmit={search}>
        <input ref={searchInput} type="search" value={query} onChange={event => { suppressClick.current = false; setQuery(event.target.value); }} aria-label="Kunde suchen" placeholder="Name oder Telefonnummer" autoComplete="off" enterKeyHint="search" />
        <button type="button" aria-label="Suchfeld schließen" onClick={closeSearch}>✕</button>
        <button type="submit" aria-label="Suche starten" onPointerDown={event => { suppressClick.current = false; event.preventDefault(); }}><FiArrowRight size={19} strokeWidth={1.6} aria-hidden="true" /></button>
      </form>
      {!searchExpanded && <>
      <div
        className="workflow-status"
        aria-label="Offene Aufgaben je Studio. Für die Kundensuche nach links wischen."
        tabIndex={0}
        onKeyDown={event => { if (event.key === 'ArrowLeft') { event.preventDefault(); openSearch(); } }}
      >
        {STUDIOS.map(studio => (
          <div key={studio.id} className="workflow-location-group">
            <button type="button" onClick={() => navigate(`/consultations?tasks=1&locationId=${studio.id}`)} className="workflow-location" title={actionLabel(studio.name, actionCounts[studio.id])} aria-label={actionLabel(studio.name, actionCounts[studio.id])}>{studio.short}</button>
            <ConsultationActionBadges count={actionCounts[studio.id]} studioId={studio.id} counts={actionCounts} />
          </div>
        ))}
      </div>

      <span
        className="workflow-divider"
        aria-hidden="true"
      />

      <button
        type="button"
        onClick={() => navigate('/')}
        className={`workflow-button${
          isBooking ? ' active' : ''
        }`}
        aria-label="Bookings"
        title="Bookings"
      >
        <FiCalendar />
      </button>

      <button
        type="button"
        onClick={() => navigate(actionTarget(actionCounts))}
        className={`workflow-button${
          isConsultations
            ? ' active'
            : ''
        }`}
        aria-label={actionLabel('Beratungen', totalActions)}
        title="Beratungen"
      >
        <FiMessageSquare />
      </button>
      {['owner', 'manager'].includes(user?.role) && <button type="button"
        className={'workflow-button workflow-chat-button' + (location.pathname === '/chat' ? ' active' : '')}
        onClick={() => navigate(unreadDestination(chatUnread.counts, chatUnread.dmLocations))}
        aria-label={'Chat, ' + chatUnread.total + ' ungelesene Nachrichten'} title="Chat">
        <FiMessageCircle /><ChatBadge count={chatUnread.total} />
      </button>}
      </>}
    </div>
  );
}

export default WorkflowBar;
// prime-ink-cancellation-alerts-v1
